import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { Form, Button, Radio, Tooltip } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import Actions from '../../../../actions'
import MaterialInput from '../../../common/MaterialInput'
import $t from '../../../../i18n'
import {
  FormFields,
  dateFormat,
  capitalizeFirstLetter,
  COUNTRY_CODES,
  signUpFlowTypes,
} from '../../../../consts'
import { isProduction } from '../../../../utils/envUtils'
import config from '../../../../config.json'
import Dialog from '../../../common/Dialog'
import { event, eventTypes } from '../../../../consts/eventConsts'
import validateZipcode from '../../../../utils/validateZipcode'
import VerifyPhoneCode from '../../../pages/VerifyPhoneCode'
import Page from '../../../common/Page/Page'
import PageCard from '../../../common/PageCard'
import DatePicker from '../../../common/DatePicker'
import { getUrlParams } from '../../../../utils/useParamUtil'

import './SignupForm.css'

const SignupForm = props => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const { memberId, subscriptionId, transactionId } = getUrlParams()

  const phoneNumberRegex = /^\d{10}$/

  const [showUnderAgeDialog, setShowUnderAgeDialog] = React.useState(false)
  const [error, setError] = React.useState(null)

  const [selectedDate, setSelectedDate] = useState(null)
  const {
    isSignupPhoneNumberLoading,
    signupPhoneNumberErrorMessage,
    isSignupPhoneNumberSuccess,
    isVerificationCodeValid,
    isVerificationCodeInvalid,
    emailLoading,
  } = useSelector(state => state.signup)
  const signupFlowType = useSelector(state => state.auth?.signupFlowType)
  const declareSignUpFlowType = useSelector(
    state => state.auth?.declareSignUpFlowType
  )
  const prefillFieldsForSignup = useSelector(
    state => state.signup?.prefillFieldsForSignup
  )
  const urlParams = useSelector(state => state.signup?.urlParams)
  const signupErrorMessage = useSelector(
    state => state.signup?.signupFormErrorMessage
  )

  const { getFieldValue, validateFields, getFieldError } = form

  const validateFieldErrors = async (validateFields, fieldName) => {
    try {
      await validateFields([FormFields.phoneNumber])
    } catch (e) {
      console.log(e)
    }
    return
  }
  const validAge = date => {
    // if HMO flow, no age validation, and if it's a minor who's not a cardholder - we'll receive
    // a rejection from the server on classify-user
    if (
      declareSignUpFlowType === signUpFlowTypes.HMO ||
      prefillFieldsForSignup
    ) {
      return true
    }
    const age = moment().diff(date, 'years')
    return age >= config.minimalAdultAge
  }
  const validateDOB = (rule, value, callback) => {
    let currentYear = new Date().getFullYear()
    let year = new Date(selectedDate).getFullYear()
    if (!selectedDate) {
      callback($t('pages.getStarted.signupForm.errors.pleaseInputDOB'))
    } else if (
      year.toString().length < 4 ||
      year < currentYear - 121 ||
      year > currentYear
    ) {
      callback($t('pages.getStarted.signupForm.errors.invalidDOB'))
    } else {
      let valid = validAge(selectedDate)
      if (valid) {
        callback()
      } else {
        setShowUnderAgeDialog(true)
      }
    }
  }

  const validateZip = (rule, value, callback) => {
    if (!value) {
      callback($t('errors.validation.missing'))
    } else if (!validateZipcode(value)) {
      callback($t('errors.validation.invalidZipcode'))
    } else {
      callback()
    }
  }

  const validatePhoneNumber = (rule, value, callback) => {
    // const trimmedValue = value.phone.replaceAll(' ', '')
    const isValidPhoneNumber = phoneNumberRegex.test(value.phone)
    if (!isValidPhoneNumber) {
      callback(
        $t('pages.getStarted.signupForm.errors.pleaseInputValidPhoneNumber')
      )
    } else {
      callback()
    }
  }

  const validateConfirmEmail = (rule, value, callback) => {
    const email = getFieldValue(FormFields.email)
    if (!value) {
      callback($t('errors.validation.missing'))
    } else if (value !== email) {
      callback($t('pages.getStarted.signupForm.errors.emailsDontMatch'))
    } else {
      callback()
    }
  }

  useEffect(() => {
    if (
      transactionId &&
      subscriptionId &&
      memberId &&
      !prefillFieldsForSignup
    ) {
      dispatch(
        Actions.createAction(Actions.GET_PREFILL_FIELDS_FOR_SIGNUP_REQUESTED, {
          transactionId,
          subscriptionId,
          memberId,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId])

  useEffect(() => {
    // from cms on OE we will get the prefill fields for signup
    if (prefillFieldsForSignup) {
      form.setFieldsValue({
        [FormFields.firstName]: prefillFieldsForSignup?.firstName,
        [FormFields.middleName]: prefillFieldsForSignup?.middleInitial,
        [FormFields.lastName]: prefillFieldsForSignup?.lastName,
        [FormFields.email]: prefillFieldsForSignup?.email,
        [FormFields.emailConfirm]: prefillFieldsForSignup?.email,
        [FormFields.zip]: prefillFieldsForSignup?.zip,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillFieldsForSignup])

  useEffect(() => {
    setError(signupErrorMessage)
  }, [signupErrorMessage])

  // if verification code is valid, end the signup process
  useEffect(() => {
    if (isVerificationCodeValid) {
      const phoneNumber = getFieldValue(FormFields.phoneNumber)
      const email = getFieldValue(FormFields.email)
      const firstName = getFieldValue(FormFields.firstName)
      const lastName = getFieldValue(FormFields.lastName)
      const dateOfBirth = moment(selectedDate).format(dateFormat.dob)
      handlePhoneNumberVerificationStep({
        phoneNumber,
        email,
        firstName,
        lastName,
        dateOfBirth,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerificationCodeValid])

  const sendVerificationCode = async () => {
    const phoneNumber = getFieldValue(FormFields.phoneNumber)
    const email = getFieldValue(FormFields.email)
    const firstName = getFieldValue(FormFields.firstName)
    const lastName = getFieldValue(FormFields.lastName)
    const dob = moment(selectedDate).format(dateFormat.dob)
    const middleName = getFieldValue(FormFields.middleName)
    const zipCode = getFieldValue(FormFields.zip)

    await validateFieldErrors(validateFields, FormFields.email)
    await validateFieldErrors(validateFields, FormFields.phoneNumber)
    const fieldError = getFieldError(FormFields.phoneNumber)

    if (fieldError.length === 0) {
      window.document.cookie = 'antidote=antidote;'
      let data = {
        email,
        phoneNumber: `+${phoneNumber?.code}${phoneNumber?.phone}`,
        firstName,
        lastName,
        middleName,
        dob,
        zipCode,
        urlParams,
        transactionId,
      }
      dispatch(Actions.createAction(Actions.CLASSIFY_USER_REQUESTED, data))
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.submit,
          data: data,
          element: 'next information account setup',
        })
      )
    }
  }

  const renderPhoneNumberErrorMessage = () => (
    <span className="signup-phone-number-error-message">
      {signupPhoneNumberErrorMessage}
    </span>
  )

  const getVerifyTheCode = async verificationCode => {
    const phoneNumberObj = getFieldValue(FormFields.phoneNumber)
    const phoneNumber = `+${phoneNumberObj.code}${phoneNumberObj.phone}`
    const email = getFieldValue(FormFields.email)
    dispatch(
      Actions.createAction(Actions.SIGNUP_CODE_VERIFY_REQUESTED, {
        email,
        phoneNumber,
        value: verificationCode,
        element: 'Enter_the_verification_code',
      })
    )
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.formEvent,
        eventName: event.formEvent.submit,
        data: { email, phoneNumber },
        element: 'next whats the code?',
      })
    )
  }

  const handlePhoneNumberVerificationStep = data => {
    dispatch(
      Actions.createAction(
        Actions.SIGNUP_PHONE_NUMBER_VERIFICATION_PROCESS_COMPLETED,
        {
          ...data,
          phoneNumber: `+${data.phoneNumber?.code}${data.phoneNumber?.phone}`,
        }
      )
    )
  }

  const renderFirstNameField = () => (
    <Form.Item
      className="form-item signup-form-item-firstname"
      name={FormFields.firstName}
      type="text"
      onBlur={e =>
        form.setFieldsValue({
          [FormFields.firstName]: capitalizeFirstLetter(e.target.value),
        })
      }
      rules={[
        {
          required: true,
          message: $t(
            'pages.getStarted.signupForm.errors.pleaseInputYourFirstName'
          ),
        },
        isProduction
          ? {
              pattern: new RegExp('^[a-zA-Z-\u0590-\u05FF ]+$'),
              message: $t('errors.validation.nameLettersOnly'),
            }
          : {},
      ]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.firstName}
        className="signup-form-firstname signup-form-input"
        label={$t('pages.getStarted.signupForm.firstName')}
      />
    </Form.Item>
  )
  const renderEmailField = () => (
    <Form.Item
      className="form-item signup-form-item-email"
      name={FormFields.email}
      type="email"
      rules={[
        {
          required: true,
          type: 'email',
          message: $t('pages.getStarted.invalidEmail'),
        },
      ]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.email}
        className="signup-form-firstname signup-form-input"
        label={$t('pages.getStarted.signupForm.email')}
      />
    </Form.Item>
  )

  const renderEmailConfirmField = () => (
    <Form.Item
      className="form-item signup-form-item-email"
      name={FormFields.emailConfirm}
      type="email"
      rules={[{ validator: validateConfirmEmail }]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.email}
        className="signup-form-firstname signup-form-input"
        label={$t('pages.getStarted.signupForm.emailConfirm')}
      />
    </Form.Item>
  )

  const renderZipField = () => (
    <Form.Item
      className="form-item signup-form-item-zip"
      name={FormFields.zip}
      type="zip"
      rules={[{ validator: validateZip }]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.zip}
        className="signup-form-zip signup-form-input"
        label={$t('pages.getStarted.signupForm.zip')}
      />
    </Form.Item>
  )

  const renderLastNameField = () => (
    <Form.Item
      className="form-item signup-form-item-lastname"
      name={FormFields.lastName}
      type="text"
      onBlur={e =>
        form.setFieldsValue({
          [FormFields.lastName]: capitalizeFirstLetter(e.target.value),
        })
      }
      rules={[
        {
          required: true,
          message: $t(
            'pages.getStarted.signupForm.errors.pleaseInputYourLastName'
          ),
        },
        isProduction
          ? {
              pattern: new RegExp('^[a-zA-Z-\u0590-\u05FF ]+$'),
              message: $t('errors.validation.nameLettersOnly'),
            }
          : {},
      ]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.lastName}
        className="signup-form-lastname signup-form-input"
        label={$t('pages.getStarted.signupForm.lastName')}
      />
    </Form.Item>
  )

  const renderMiddleNameField = () => (
    <Form.Item
      className="form-item signup-form-item-firstname"
      name={FormFields.middleName}
      type="text"
      rules={[
        isProduction
          ? {
              pattern: new RegExp('^[a-zA-Z-\u0590-\u05FF ]+$'),
              message: $t('errors.validation.nameLettersOnly'),
            }
          : {},
      ]}
    >
      <MaterialInput
        disabled={prefillFieldsForSignup?.middleInitial}
        className="signup-form-middlename signup-form-input"
        label={$t('pages.getStarted.signupForm.middleName')}
      />
    </Form.Item>
  )

  const renderDobField = () => (
    <div className="date-picker-with-label">
      <Form.Item
        name={FormFields.dateOfBirth}
        className="date-of-birth-picker"
        type="date"
        rules={[{ validator: validateDOB }]}
      >
        <DatePicker
          setSelectedDate={setSelectedDate}
          className="signup-form-dob signup-form-input"
          label={$t('pages.getStarted.signupForm.dateOfBirth')}
        />
      </Form.Item>
    </div>
  )

  const renderPhoneNumberField = () => (
    <div className="phone-number-fields-container">
      <div className="signup-phone-number-container">
        <Form.Item
          className="form-item"
          name={FormFields.phoneNumber}
          type="text"
          rules={[{ validator: validatePhoneNumber }]}
        >
          <MaterialInput
            className="one-time-login-phonenumber"
            label={$t('pages.getStarted.signupForm.phoneNumber')}
            hideLabel
            type="phone"
          />
        </Form.Item>
      </div>
    </div>
  )

  const renderNextButton = () => (
    <Form.Item className="signup-next-button-container">
      <Button
        className="signup-next-button trackable"
        data-name="signup next button"
        loading={emailLoading}
        disabled={!declareSignUpFlowType && !prefillFieldsForSignup}
        htmlType="submit"
        type="primary"
      >
        {$t('pages.getStarted.signupForm.next')}
      </Button>
    </Form.Item>
  )

  const defaultPhoneNumber = isProduction
    ? {
        short: 'US',
        code: COUNTRY_CODES[0],
        phone: '',
      }
    : {
        code: 972,
        phone: '',
        short: 'IL',
      }

  const renderSignupForm = () => (
    <Form
      id="signup-form"
      className="signup-form trackable"
      data-name="Sign-up"
      onFinish={sendVerificationCode}
      name="basic"
      onFinishFailed={error => {
        dispatch(
          Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
            type: eventTypes.formEvent,
            eventName: event.formEvent.invalid,
            element: 'signup-form',
            data: error.values,
            error: error,
          })
        )
      }}
      initialValues={{
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        zip: '',
        phoneNumber: defaultPhoneNumber,
      }}
      form={form}
    >
      <div className="signup-steps-container">
        <div className="signup-step-container left">
          <div className="signup-group-fields">
            {renderEmailField()} {renderEmailConfirmField()}
          </div>
          <div className="signup-group-fields">
            <div className="field-title">
              {$t('pages.getStarted.signupForm.legalName')}
            </div>
            {renderFirstNameField()}
            {renderMiddleNameField()}
            {renderLastNameField()}
          </div>
        </div>
        <div className="signup-step-container right">
          <div className="signup-group-fields">{renderZipField()}</div>
          <div className="signup-group-fields dob">
            <div className="field-title">
              {$t('pages.getStarted.signupForm.dateOfBirth')}
            </div>
            {renderDobField()}
          </div>
          <div className="signup-group-fields phone">
            <div className="field-title">
              {$t('pages.getStarted.signupForm.phoneNumber')}
            </div>
            <div className="field-sub">
              {$t('pages.getStarted.signupForm.wellTextYouAVerificationCode')}
            </div>
            {renderPhoneNumberField()}
          </div>
        </div>
      </div>
      {error && <div className="signup-server-errors">{error}</div>}
      {signupPhoneNumberErrorMessage && renderPhoneNumberErrorMessage()}
      <div className="row signup-flow-footer">
        {!prefillFieldsForSignup && (
          <Radio.Group
            onChange={e =>
              dispatch(
                Actions.createAction(
                  Actions.DECLARE_SIGNUP_FLOW_TYPE,
                  e.target.value
                )
              )
            }
            value={declareSignUpFlowType}
          >
            <Radio value={signUpFlowTypes.HMO}>
              <div className="row flow-option">
                <span>
                  {$t(
                    'pages.getStarted.signupForm.flowOptions.setUpAccountHmo'
                  )}
                </span>
                <span className="semi-bold">
                  {$t('pages.getStarted.signupForm.flowOptions.hmo')}
                </span>
                {$t('pages.getStarted.signupForm.flowOptions.account')}
                <Tooltip
                  title={$t(
                    'pages.getStarted.signupForm.flowOptions.insuranceInfo'
                  )}
                  placement="right"
                  color={'var(--white)'}
                  textColor={'var(--textColor)'}
                  overlayClassName="signup-info-flow-type-tooltip"
                >
                  <i className="icon-info"></i>
                </Tooltip>
              </div>
            </Radio>
            <Radio value={signUpFlowTypes.TELEHEALTH}>
              <div className="row flow-option">
                {$t('pages.getStarted.signupForm.flowOptions.setUpAccount')}
                <span className="semi-bold">
                  {$t('pages.getStarted.signupForm.flowOptions.telehealth')}
                </span>
                {$t('pages.getStarted.signupForm.flowOptions.servicesAccount')}
                <Tooltip
                  title={$t(
                    'pages.getStarted.signupForm.flowOptions.telehealthInfo'
                  )}
                  placement="right"
                  color={'var(--white)'}
                  textColor={'var(--textColor)'}
                  overlayClassName="signup-info-flow-type-tooltip"
                >
                  <i className="icon-info"></i>
                </Tooltip>
              </div>
            </Radio>
          </Radio.Group>
        )}
        {renderNextButton()}
      </div>
    </Form>
  )

  const onResendSms = () => {
    signupFlowType === signUpFlowTypes.HMO
      ? dispatch(Actions.createAction(Actions.HMO_RESEND_SMS_REQUESTED))
      : sendVerificationCode()
  }

  const renderVerificationCodePage = () => {
    const phoneNumber = getFieldValue(FormFields.phoneNumber)
    const phoneNumberString = `+${phoneNumber?.code}${phoneNumber?.phone}`
    return (
      <VerifyPhoneCode
        onNext={({ code }) => getVerifyTheCode(code)}
        onResend={onResendSms}
        phone={phoneNumberString}
        loading={isSignupPhoneNumberLoading}
        errorMessage={
          isVerificationCodeInvalid
            ? $t('pages.getStarted.signupForm.invalidCode')
            : ''
        }
      />
    )
  }

  const renderSignUpPage = () => {
    return (
      <PageCard className="signup-page-car login-page-card">
        <Page
          title={$t('pages.getStarted.signupForm.title')}
          subtitle={$t('pages.getStarted.signupForm.subtite')}
          className="signup-page login-page"
        >
          <div className="signup-form-container">{renderSignupForm()}</div>
          <Dialog
            title={$t('pages.getStarted.signupForm.errors.underAgeTitle')}
            isVisible={showUnderAgeDialog}
            className="signup-form-underage-dialog"
            footerActions={[
              <Button
                type="primary"
                onClick={() => setShowUnderAgeDialog(false)}
              >
                {$t('pages.getStarted.signupForm.gotIt')}
              </Button>,
            ]}
            closable={true}
            onCancel={() => setShowUnderAgeDialog(false)}
          >
            <div className="underage-text">
              {$t('pages.getStarted.signupForm.errors.underAgeText')}
            </div>
          </Dialog>
        </Page>
      </PageCard>
    )
  }

  return isSignupPhoneNumberSuccess
    ? renderVerificationCodePage()
    : renderSignUpPage()
}

export default SignupForm
